import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector, useDispatch } from 'react-redux';
import {
  Search,
  Bell,
  Menu,
  ChevronDown,
  User,
  Settings,
  LogOut,
  Moon,
  Sun,
  Home,
  ChevronRight,
  CalendarX,
  CalendarPlus,
} from 'lucide-react';
import { toggleSidebar, toggleDarkMode } from '../../redux/slices/uiSlice';
import LogoutModal from './LogoutModal';
import SaleEndModal from './SaleEndModal';
import StartNewDayModal from './StartNewDayModal';
import { fetchTodayStatus } from '../../redux/slices/shiftSlice';
import toast from 'react-hot-toast';

const breadcrumbMap = {
  '/': 'Dashboard',
  '/pos': 'POS',
  '/inventory': 'Inventory',
  '/customers': 'Customers',
  '/reports': 'Reports',
  '/settings': 'Settings',
  '/backup': 'Backup',
  '/printing': 'Printing',
};

export default function TopNavbar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { darkMode } = useSelector((state) => state.ui);
  const { user } = useSelector((state) => state.auth);
  const { status, businessDate } = useSelector((state) => state.shift);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [saleEndOpen, setSaleEndOpen] = useState(false);
  const [startNewDayOpen, setStartNewDayOpen] = useState(false);
  const userMenuRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchOpen && searchRef.current) searchRef.current.focus();
  }, [searchOpen]);

  const breadcrumbs = location.pathname
    .split('/')
    .filter(Boolean)
    .reduce((acc, segment, index, arr) => {
      const path = '/' + arr.slice(0, index + 1).join('/');
      const name = breadcrumbMap[path] || segment.charAt(0).toUpperCase() + segment.slice(1);
      acc.push({ name, path });
      return acc;
    }, [{ name: 'Home', path: '/' }]);

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const isAdmin = user?.role === 'ADMIN' || user?.role === 'admin';

  const handleLogout = () => {
    setUserMenuOpen(false);
    setLogoutModalOpen(true);
  };

  const handleSaleEndClick = () => {
    if (isAdmin) {
      setSaleEndOpen(true);
    } else {
      toast('SALE END is available only to the admin/owner. Please contact the owner to end today\'s sale.', {
        icon: '🔒',
      });
    }
  };

  const handleStartNewDayClick = () => {
    if (isAdmin) {
      setStartNewDayOpen(true);
    } else {
      toast('START NEW DAY is available only to the admin/owner. Please contact the owner to open the shop.', {
        icon: '🔒',
      });
    }
  };

  return (
    <header className="sticky top-0 z-20 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="flex items-center justify-between h-16 px-4 lg:px-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => dispatch(toggleSidebar())}
            className="md:hidden flex items-center justify-center w-9 h-9 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <Menu size={20} />
          </button>

          <nav className="hidden sm:flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
            {breadcrumbs.map((crumb, idx) => (
              <React.Fragment key={crumb.path}>
                {idx > 0 && <ChevronRight size={14} className="mx-1" />}
                <button
                  onClick={() => navigate(crumb.path)}
                  className={`hover:text-gray-700 dark:hover:text-gray-200 transition-colors ${
                    idx === breadcrumbs.length - 1
                      ? 'text-gray-900 dark:text-white font-medium'
                      : ''
                  }`}
                >
                  {idx === 0 ? <Home size={14} /> : crumb.name}
                </button>
              </React.Fragment>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-2 lg:gap-3">
          {(status === 'OPEN' || status === 'CLOSED' || status === 'NO_DAY') && (
            <span
              className={`hidden md:inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${
                status === 'OPEN'
                  ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600'
              }`}
              title={businessDate || ''}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${status === 'OPEN' ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'}`} />
              {status === 'OPEN' ? 'Shop Open' : 'Shop Closed'}
            </span>
          )}

          {status === 'OPEN' && (
            <button
              onClick={handleSaleEndClick}
              className="hidden md:inline-flex items-center gap-1.5 text-xs font-bold tracking-wide px-3 py-1.5 rounded-full border-2 border-red-500 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              title="Close today's business session"
            >
              <CalendarX size={13} />
              SALE END
            </button>
          )}

          {status !== 'OPEN' && (
            <button
              onClick={handleStartNewDayClick}
              className="hidden md:inline-flex items-center gap-1.5 text-xs font-bold tracking-wide px-3 py-1.5 rounded-full border-2 border-emerald-500 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
              title="Open the shop for a new business day"
            >
              <CalendarPlus size={13} />
              START NEW DAY
            </button>
          )}

          <div className="hidden md:flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mr-2">
            <span className="hidden lg:inline">{formatDate(currentTime)}</span>
            <span className="font-medium text-gray-700 dark:text-gray-300">{formatTime(currentTime)}</span>
          </div>

          <div className="relative">
            <AnimatePresence mode="wait">
              {searchOpen ? (
                <motion.div
                  key="search-expanded"
                  initial={{ width: 0, opacity: 0 }}
                  animate={{ width: 280, opacity: 1 }}
                  exit={{ width: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="relative"
                >
                  <input
                    ref={searchRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onBlur={() => !searchQuery && setSearchOpen(false)}
                    placeholder="Search anything..."
                    className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <Search size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                </motion.div>
              ) : (
                <motion.button
                  key="search-closed"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setSearchOpen(true)}
                  className="flex items-center justify-center w-9 h-9 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <Search size={20} />
                </motion.button>
              )}
            </AnimatePresence>
          </div>

          <button className="relative flex items-center justify-center w-9 h-9 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <Bell size={20} />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
          </button>

          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/50 flex items-center justify-center">
                <span className="text-sm font-semibold text-primary-700 dark:text-primary-300">
                  {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                </span>
              </div>
              <span className="hidden lg:block text-sm font-medium text-gray-700 dark:text-gray-200">
                {user?.name || 'User'}
              </span>
              <ChevronDown size={16} className="hidden lg:block text-gray-400" />
            </button>

            <AnimatePresence>
              {userMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.96 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50"
                >
                  <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {user?.name || 'User'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {user?.email || 'user@haiderpanshop.com'}
                    </p>
                  </div>

                  <button
                    onClick={() => { setUserMenuOpen(false); navigate('/settings'); }}
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <User size={16} className="text-gray-400" />
                    View Profile
                  </button>

                  <button
                    onClick={() => { setUserMenuOpen(false); navigate('/settings'); }}
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <Settings size={16} className="text-gray-400" />
                    Settings
                  </button>

                  <button
                    onClick={() => dispatch(toggleDarkMode())}
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    {darkMode ? (
                      <Sun size={16} className="text-gray-400" />
                    ) : (
                      <Moon size={16} className="text-gray-400" />
                    )}
                    {darkMode ? 'Light Mode' : 'Dark Mode'}
                  </button>

                  <div className="border-t border-gray-200 dark:border-gray-700 mt-1 pt-1">
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      <LogOut size={16} />
                      Logout
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
      <LogoutModal isOpen={logoutModalOpen} onClose={() => setLogoutModalOpen(false)} />
      <SaleEndModal isOpen={saleEndOpen} onClose={() => setSaleEndOpen(false)} />
      <StartNewDayModal isOpen={startNewDayOpen} onClose={() => setStartNewDayOpen(false)} />
    </header>
  );
}
